import React, { FC, useState } from 'react';
import TwTooltip from '../../../TwTooltip/TwTooltip';
import Box from '@mui/material/Box';
import NumberUtils from '../../../../utils/numberUtils';
import LoadUtils from '../../../../utils/loadUtils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadRate } from '../../../../redux/api/load/load.model';
import { TwButton, TwButtonType } from '../../../TwButtons/TwButtons';
import { InvoiceServiceUnit } from '../../../../redux/api/invoiceService/invoiceService.model';
import { Trip } from '../../../../redux/api/trips/trips.model';
import { OrderRate } from '../../../../redux/api/orders/orders.model';
import { USER_ROLE } from '../../../../providers/User/User.model';
import { RateTableAttributes } from '../../../../redux/api/rateTable/rateTable.model';
import { Typography } from '@mui/material';
import useAuth from '../../../../providers/Auth/Auth.provider';

const RATE_TOOL_TIP_TEXT_ESTIMATED_BY_ROUTE = 'Calculated using loaded mileage from route info';
const RATE_TOOL_TIP_TEXT_ESTIMATED_BY_ZIPCODE =
  'Calculated using estimated mileage based on zip codes';

interface LoadRateAndTotalRatesProps {
  loadSegments: Trip[];
  rate: number | null;
  load_rates: OrderRate[];
  rate_per_mile: number | null;
  role: USER_ROLE | undefined;
  rateTable?: RateTableAttributes | null;
}

const LoadRateAndTotalRates: FC<LoadRateAndTotalRatesProps> = ({
  loadSegments,
  rate,
  load_rates,
  rate_per_mile,
  role,
  rateTable
}) => {
  const { getTenantSetting } = useAuth();

  const isUseRateTableAppSettingEnabled = getTenantSetting('use_rate_table_for_orders');
  const [show, setShow] = useState(false);

  const handleHideShow = (): void => {
    setShow(!show);
  };

  const getTotalRate = (
    rate: number | null,
    quantity: number,
    unit: InvoiceServiceUnit | null,
    qboServiceId: string | null = null,
    invoiceServiceId: string | null = null
  ): string => {
    if (!rate) {
      return '';
    }

    if (unit === InvoiceServiceUnit.FLAT) {
      return LoadUtils.formatRate(rate);
    }

    if (
      unit === InvoiceServiceUnit.PERCENTAGE &&
      (qboServiceId !== '' || qboServiceId !== null) &&
      invoiceServiceId !== '' &&
      invoiceServiceId !== null
    ) {
      return LoadUtils.formatRate(
        NumberUtils.calculateCurrencyAmountWithPrecision(rate / 100, quantity)
      );
    }
    return LoadUtils.formatRate(NumberUtils.calculateCurrencyAmountWithPrecision(rate, quantity));
  };

  const showRateUnit = (unit: string | null): string => {
    switch (unit) {
      case InvoiceServiceUnit.PER_MILE:
        return 'per mi';
      case InvoiceServiceUnit.PER_HOUR:
        return 'per hour';
      case InvoiceServiceUnit.PER_STOP:
        return 'per stop';
      case InvoiceServiceUnit.PER_DAY:
        return 'per day';
      case InvoiceServiceUnit.PER_UNIT:
        return 'per unit';
      case InvoiceServiceUnit.FLAT:
        return '';
      default:
        return '';
    }
  };
  return (
    <>
      <Box component="div" className="load-detail__data-container__overview--sub-title">
        {load_rates && load_rates.length > 0 ? 'Total Rate' : 'Rate'}
        {!!rate_per_mile &&
          load_rates.length >= 0 &&
          (role === USER_ROLE.CUSTOMER_CONTACT || !role) && (
            <TwTooltip
              title="This total rate is an estimate and is subject to change"
              placement="top"
            >
              <InfoOutlinedIcon sx={{ height: '16px' }} />
            </TwTooltip>
          )}
      </Box>
      {isUseRateTableAppSettingEnabled && rateTable && rateTable.name && (
        <Typography variant="caption" sx={{ mb: 0.5, fontSize: '14px', color: '#1F2937' }}>
          Rate table: <br />
          {rateTable.name}
        </Typography>
      )}
      <Box
        component="div"
        className="load-detail__data-container__overview__rate"
        sx={{ paddingBottom: load_rates.length > 0 ? '0 !important' : '20px' }}
      >
        {LoadUtils.formatRate(rate ? NumberUtils.roundNumber(rate, 2) : null)}
        {rate_per_mile && load_rates.length >= 0 && role && role !== USER_ROLE.CUSTOMER_CONTACT && (
          <>
            <Box component="span">&nbsp; / {NumberUtils.getCurrency(rate_per_mile)} mi</Box>
            <TwTooltip
              title={
                (loadSegments && loadSegments[0]?.stops[0].location?.is_zip_approximated) ||
                (loadSegments &&
                  loadSegments[0]?.stops[loadSegments[0]?.stops.length - 1].location
                    ?.is_zip_approximated)
                  ? RATE_TOOL_TIP_TEXT_ESTIMATED_BY_ZIPCODE
                  : RATE_TOOL_TIP_TEXT_ESTIMATED_BY_ROUTE
              }
              placement="top"
            >
              <InfoOutlinedIcon sx={{ height: '16px' }} />
            </TwTooltip>
          </>
        )}
      </Box>
      {load_rates.length > 0 && (
        <TwButton
          label={!show ? 'Show breakdown' : 'Hide breakdown'}
          action={handleHideShow}
          type={TwButtonType.text}
          sx={{
            width: '150px',
            padding: '0px',
            paddingBottom: '20px',
            justifyContent: 'flex-start',
            textTransform: 'none !important'
          }}
        />
      )}
      {show &&
        [...load_rates]
          .sort((r1: LoadRate, r2: LoadRate) => r1.sort_order - r2.sort_order)
          .map((rate: LoadRate) => (
            <>
              <Box component="div" className="load-detail__data-container__overview--sub-title">
                {rate.service_name}
              </Box>

              <Box component="div" className="load-detail__data-container__overview__rate">
                {getTotalRate(
                  rate.rate,
                  rate.quantity,
                  rate.unit as InvoiceServiceUnit,
                  rate.qbo_service_id,
                  rate.invoice_service_id
                )}

                {rate.quantity > 0 &&
                  rate.rate &&
                  rate.unit !== InvoiceServiceUnit.FLAT &&
                  rate.unit !== InvoiceServiceUnit.PERCENTAGE && (
                    <Box component="span">
                      &nbsp; / {NumberUtils.getCurrency(rate.rate, 4)} {showRateUnit(rate.unit)}
                    </Box>
                  )}
              </Box>
            </>
          ))}
    </>
  );
};

export default LoadRateAndTotalRates;
